import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { Button } from "@/components/ui/button";
import { NotificationItem, getNotificationPath } from "@/components/notification-item";
import { useMediaQuery } from "@/lib/use-media-query";

// 124 -- portal width must match the panel's Tailwind `w-80` (320px) so the
// position math below can right-align it to the bell.
const PANEL_WIDTH = 320;
const PANEL_GAP = 4; // ~mt-1 in Tailwind

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllRead, isLoading } =
    useNotifications();
  const navigate = useNavigate();
  // 201 -- on phones the bell pushes the full-screen /notifications list
  // (stack nav) instead of opening a desktop dropdown.
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // 124 -- panel is portaled into document.body to escape the sidebar's
  // `overflow-hidden` (needed for the collapse animation). Position is
  // computed from the bell's bounding rect when the dropdown opens.
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(null);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) markAsRead(notification.id);
    const path = getNotificationPath(notification);
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  // 124 -- compute portal position when opening, anchoring the panel's right
  // edge to the bell's right edge so it visually drops down-and-left like the
  // pre-portal `right-0 top-full` styling did.
  useEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    const rect = bellButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPos({
      top: rect.bottom + PANEL_GAP,
      left: Math.max(8, rect.right - PANEL_WIDTH),
    });
  }, [open]);

  // Close on click outside -- check both the wrapper (bell + chrome) AND the
  // portaled panel, since the panel is no longer a descendant of the wrapper.
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insidePanel = panelRef.current?.contains(target);
      if (!insideWrapper && !insidePanel) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // Close on escape
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const recentNotifications = notifications.slice(0, 10);

  return (
    <div className="relative" ref={wrapperRef}>
      {/* Bell button */}
      <Button
        ref={bellButtonRef}
        variant="ghost"
        size="sm"
        className="relative h-8 w-8 p-0"
        onClick={() => (isMdUp ? setOpen((prev) => !prev) : navigate("/notifications"))}
        title="Notifications"
        data-action="notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown panel -- portaled to document.body so it isn't clipped by
          the sidebar's `overflow-hidden`. Position is fixed, computed from
          the bell's bounding rect. */}
      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: panelPos.top, left: panelPos.left, width: PANEL_WIDTH }}
          className="z-50 overflow-hidden rounded-lg border border-border bg-background shadow-lg"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="text-sm font-medium">Notifications</span>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-2 py-1 text-xs text-primary hover:text-primary"
                onClick={() => {
                  markAllRead();
                }}
              >
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {isLoading && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                Loading...
              </p>
            )}
            {!isLoading && recentNotifications.length === 0 && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                No notifications yet
              </p>
            )}
            {!isLoading &&
              recentNotifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onClick={handleNotificationClick}
                />
              ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
